// DiarySiblingsNav — prev/next diary nav (W32 polish).
//
// Mirrors Anniversary's SiblingsNav: a 2-col grid with prev / next cards.
// Disabled state (no neighbour) matches the iOS "no more content" pattern
// (faded chevron + muted text, per SiblingsNav.tsx).
//
// Sibling lookup is client-side over the in-memory `list` (already fetched
// for the diary list page); the active detail page does NOT issue a new
// list query.  This keeps the detail page cheap + deterministic.  When the
// active list grows past 1 page, the prev / next pair may include
// off-screen rows — acceptable per W32 polish scope (no backend cursor
// awareness yet).

import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils/cn";
import { emojiForCode } from "@/lib/diary/mood";
import type { DiaryOut } from "@/lib/api/diary";

export interface DiarySiblingsNavProps {
  list: readonly DiaryOut[];
  currentId: string;
}

interface SiblingEntry {
  id: string;
  title: string | null;
  date: string;
  preview: string;
  mood: DiaryOut["mood"];
}

function summarise(d: DiaryOut): SiblingEntry {
  const stripped = d.content.replace(/<[^>]+>/g, " ");
  const preview = (d.title ? `${d.title} · ${stripped}` : stripped).slice(0, 80) || "（空白日記）";
  return {
    id: d.id,
    title: d.title,
    date: d.date,
    preview,
    mood: d.mood,
  };
}

export function DiarySiblingsNav({ list, currentId }: DiarySiblingsNavProps) {
  if (list.length < 2) return null;
  const idx = list.findIndex((d) => d.id === currentId);
  if (idx === -1) return null;
  const prev = idx < list.length - 1 ? list[idx + 1] : null;
  const next = idx > 0 ? list[idx - 1] : null;

  if (!prev && !next) return null;

  return (
    <section
      data-component="DiarySiblingsNav"
      aria-label="相鄰日記"
      data-testid="diary-siblings-nav"
      className="grid grid-cols-2 gap-3"
    >
      <SiblingCard side="prev" item={prev ? summarise(prev) : null} emptyLabel="沒有更早的日記" />
      <SiblingCard side="next" item={next ? summarise(next) : null} emptyLabel="沒有更新的日記" />
    </section>
  );
}

interface SiblingCardProps {
  side: "prev" | "next";
  item: SiblingEntry | null;
  emptyLabel: string;
}

function SiblingCard({ side, item, emptyLabel }: SiblingCardProps) {
  const Icon = side === "prev" ? ChevronLeft : ChevronRight;
  if (!item) {
    return (
      <div
        data-testid={`diary-sibling-${side}`}
        data-empty="true"
        aria-disabled="true"
        className={cn(
          "h-20 rounded-md bg-paper-calm/60 flex items-center gap-2 px-3",
          "text-meta text-ink-3 select-none",
        )}
      >
        {side === "prev" ? <Icon size={16} aria-hidden className="text-ink-3/50" /> : null}
        <span className="flex-1 text-center">{emptyLabel}</span>
        {side === "next" ? <Icon size={16} aria-hidden className="text-ink-3/50" /> : null}
      </div>
    );
  }
  return (
    <Link
      to={`/diaries/${item.id}`}
      data-testid={`diary-sibling-${side}`}
      className={cn(
        "h-20 rounded-md glass border border-hairline p-3 flex items-center gap-2 press",
        "hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2",
      )}
    >
      {side === "prev" ? <Icon size={16} aria-hidden className="text-ink-2 shrink-0" /> : null}
      <div className="flex-1 min-w-0 flex flex-col">
        <p className="text-meta text-ink-3 tabular-nums">
          {item.date}
          {item.mood ? (
            <span aria-hidden className="ml-1">
              {emojiForCode(item.mood)}
            </span>
          ) : null}
        </p>
        {/* `line-clamp-2` — this is a diary title, i.e. the user's own words,
            and the row is a flex column that already has a date line above it.
            At 320px `truncate` cut it to 88px of 121px; a second line costs
            nothing structurally here because the date line above is fixed. */}
        <p className="text-body-sm text-ink-1 line-clamp-2 text-balance">
          {item.title ?? item.preview}
        </p>
      </div>
      {side === "next" ? <Icon size={16} aria-hidden className="text-ink-2 shrink-0" /> : null}
    </Link>
  );
}
